import { MouseEvent, ReactNode } from 'react';

import clsx from 'clsx';

import { DestructiveDialogResult } from './types';
import { Button, ButtonProps } from '../../../../components/buttons';
import { Scroller, ScrollerProps } from '../../../../components/scroller';
import { tokens } from '../../../../theme/generated/tokens';
import { classNamesFor } from '../../../../utils/classNames';
import { Inset, ActionsGroup } from '../../../containers';
import { TypographyOrNode } from '../../../utils';
import { DialogCard, DialogCardProps } from '../../components/DialogCard';
import { DialogFooter, DialogFooterProps } from '../../components/DialogFooter';
import { DialogHeader, DialogHeaderProps } from '../../components/DialogHeader';
import { useCloseDialog } from '../../hooks/useCloseDialog';

const classNamesFactory = classNamesFor('DestructiveDialog');

const classNames = {
  root: classNamesFactory.root(),
  title: classNamesFactory.element('Title'),
  message: classNamesFactory.element('Message'),
  cancelButton: classNamesFactory.element('CancelButton'),
  confirmButton: classNamesFactory.element('ConfirmButton'),
};

export type DestructiveDialogProps<RESULT = unknown> = {
  /**
   * Dialog title, displayed at the start of the header
   */
  title: ReactNode;
  /**
   * Metadata content displayed below the title in the header
   */
  metadata?: ReactNode;
  /**
   * Message of the dialog. If a string is passed, it is wrapped with `Typography`
   */
  message?: ReactNode;
  /**
   * Text, to be displayed on the confirmation button
   */
  confirmButtonText?: string;
  /**
   * Text, to be displayed on the cancel button
   */
  cancelButtonText?: string;
  /**
   * Should the dialog be closed, on clicking the confirmation button
   */
  closeOnConfirm?: boolean;
  /**
   * Callback to be invoked on confirmation button click
   * By default, the dialog is closed on confirmation button click
   */
  onConfirm: (event: MouseEvent) => void;
  /**
   * Should the dialog be closed, on clicking the cancel
   */
  closeOnCancel?: boolean;
  /**
   * Callback to be invoked on cancel button click
   * By default, the dialog is closed on cancel button click
   */
  onCancel?: (event: MouseEvent) => void;
  /**
   * Should divider be displayed at the top of the footer
   * @default false
   */
  footerDivider?: boolean;
  testId?: string;
  slotProps?: {
    root?: DialogCardProps;
    header?: DialogHeaderProps;
    scroller?: ScrollerProps;
    footer?: DialogFooterProps;
    cancelButton?: ButtonProps;
    confirmButton?: ButtonProps;
  };
} & DialogCardProps<RESULT>;

/**
 * Destructive dialog variant with mandatory title, message and footer with confirm and cancel button
 */
export const DestructiveDialog = <RESULT = unknown,>({
  title,
  metadata,
  message = 'This action cannot be undone.',
  confirmButtonText = 'Ok',
  cancelButtonText = 'Cancel',
  closeOnConfirm = true,
  onConfirm,
  closeOnCancel = true,
  result,
  onCancel,
  footerDivider = false,
  slotProps,
  testId = 'destructive-dialog',
}: DestructiveDialogProps<RESULT>) => {
  const closeDialog = useCloseDialog();
  return (
    <DialogCard
      testId={testId}
      {...slotProps?.root}
      result={result}
      className={clsx(classNames.root, slotProps?.root?.className)}
    >
      <DialogHeader
        title={title}
        metadata={metadata}
        slotProps={{ startContentWrapper: { className: classNames.title } }}
        {...slotProps?.header}
      />
      <Scroller {...slotProps?.scroller}>
        <Inset py={false}>
          <TypographyOrNode
            variant="P12R"
            className={classNames.message}
            sx={{ color: tokens.theme.fg.structural.secondary }}
            component="div"
          >
            {message}
          </TypographyOrNode>
        </Inset>
      </Scroller>
      <DialogFooter
        divider={footerDivider}
        endContent={
          <ActionsGroup>
            <Button
              variant="secondary"
              className={classNames.cancelButton}
              onClick={(event) => {
                if (closeOnCancel) {
                  closeDialog(DestructiveDialogResult.CANCELED);
                }
                onCancel?.(event);
              }}
              {...(slotProps?.cancelButton as Omit<ButtonProps, 'component'>)}
            >
              {cancelButtonText}
            </Button>
            <Button
              variant="danger"
              className={classNames.confirmButton}
              onClick={(event) => {
                if (closeOnConfirm) {
                  closeDialog(DestructiveDialogResult.CONFIRMED);
                }
                onConfirm?.(event);
              }}
              {...(slotProps?.confirmButton as Omit<ButtonProps, 'component'>)}
            >
              {confirmButtonText}
            </Button>
          </ActionsGroup>
        }
        {...slotProps?.footer}
      />
    </DialogCard>
  );
};

DestructiveDialog.classNames = classNames;
